// Watch Together store — ephemeral social state, intentionally independent from media playback state.
import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { api, type WatchTogetherCommandResult, type WatchTogetherCommandSummary, type WatchTogetherMessage, type WatchTogetherParticipant, type WatchTogetherSnapshot } from '@/lib/api';
import type { CatchUpStatus, WatchTogetherPlaybackSample } from '@/lib/player/watchTogetherPlayback';
import { log } from '@/lib/log';

type BringLiveHandler = (command: { id: string; consentExpiresAt: number; playbackId: string }) => Promise<CatchUpStatus>;

const HEARTBEAT_MS = 5_000;
const SAMPLE_STALE_MS = HEARTBEAT_MS * 2;
const REACTION_VISIBLE_MS = 20_000;
const STAGE_REACTION_VISIBLE_MS = 3_500;
const STAGE_CHAT_VISIBLE_MS = 8_000;

export const useWatchTogetherStore = defineStore('watchTogether', () => {
    const playbackId = ref<string | null>(null);
    const participants = ref<WatchTogetherParticipant[]>([]);
    const messages = ref<WatchTogetherMessage[]>([]);
    const reactions = ref<{ id: number; name: string; reaction: string }[]>([]);
    // Opt-in because a reaction can reveal an event before another viewer sees it.
    const stageReactionsEnabled = ref(false);
    const stageReactions = ref<{ id: number; name: string; reaction: string }[]>([]);
    const stageChatEnabled = ref(false);
    const stageMessages = ref<WatchTogetherMessage[]>([]);
    const lastCatchUpRequest = ref<WatchTogetherCommandSummary | null>(null);
    const eligibleOtherSessionCount = ref(0);
    const connected = ref(false);
    const unavailable = ref(false);
    // View-local UI can use this to yield when a player-owned consent prompt is active.
    const consentPromptActive = ref(false);
    let source: EventSource | null = null;
    let heartbeatTimer: number | null = null;
    let playbackSample: WatchTogetherPlaybackSample | null = null;
    let lastPlaybackEventAt = 0;
    let playbackReporter: (() => WatchTogetherPlaybackSample | null) | null = null;
    let catchUpHandler: BringLiveHandler | null = null;
    let generation = 0;
    const seenCommands = new Set<string>();
    const seenStageChatMessages = new Set<string>();

    const participantCount = computed(() => participants.value.length);
    const canBringLive = computed(() => eligibleOtherSessionCount.value > 0);

    function applySnapshot(snapshot: WatchTogetherSnapshot): void {
        participants.value = snapshot.participants;
        messages.value = snapshot.messages;
        eligibleOtherSessionCount.value = snapshot.eligibleOtherSessionCount;
        const latestCommand = snapshot.commands?.at(-1) ?? null;
        if (latestCommand) lastCatchUpRequest.value = latestCommand;
    }

    function closeSource(): void {
        source?.close();
        source = null;
        connected.value = false;
    }

    function sampleForHeartbeat(): WatchTogetherPlaybackSample | null {
        const sample = playbackReporter?.() ?? playbackSample;
        if (!sample) return null;
        if (lastPlaybackEventAt > 0 && Date.now() - lastPlaybackEventAt <= SAMPLE_STALE_MS) return sample;
        return {
            ...sample,
            measurement: { ...sample.measurement, quality: 'stale', secondsBehind: null },
            canCatchUp: false,
        };
    }

    async function heartbeat(expectedGeneration = generation): Promise<void> {
        const id = playbackId.value;
        const sample = sampleForHeartbeat();
        if (!id || !sample || !sample.eligible) return;
        try {
            const result = await api.watchTogether.heartbeat({
                playbackId: id,
                playbackState: sample.playbackState,
                measurement: sample.measurement,
                canCatchUp: sample.canCatchUp,
            });
            if (expectedGeneration !== generation || playbackId.value !== id) return;
            unavailable.value = false;
            applySnapshot(result.snapshot);
        } catch (error) {
            // A failed social request never changes the player or retunes it.
            if (expectedGeneration !== generation || playbackId.value !== id) return;
            unavailable.value = true;
            log.debug('watch_together.heartbeat_failed', { message: (error as Error).message });
        }
    }

    function openSource(id: string, expectedGeneration: number): void {
        closeSource();
        const next = new EventSource(`/api/watch-together/stream?playbackId=${encodeURIComponent(id)}`);
        source = next;
        next.addEventListener('snapshot', (event) => {
            if (expectedGeneration !== generation) return;
            try { applySnapshot(JSON.parse((event as MessageEvent<string>).data) as WatchTogetherSnapshot); } catch { /* ignore malformed social event */ }
        });
        next.addEventListener('chat', (event) => {
            if (expectedGeneration !== generation) return;
            try {
                const message = JSON.parse((event as MessageEvent<string>).data) as WatchTogetherMessage;
                if (!messages.value.some((entry) => entry.id === message.id)) messages.value = [...messages.value, message].slice(-100);
                if (stageChatEnabled.value) {
                    if (seenStageChatMessages.has(message.id)) return;
                    seenStageChatMessages.add(message.id);
                    if (seenStageChatMessages.size > 100) seenStageChatMessages.delete(seenStageChatMessages.values().next().value!);
                    stageMessages.value = [...stageMessages.value, message].slice(-2);
                    window.setTimeout(() => {
                        if (expectedGeneration === generation) stageMessages.value = stageMessages.value.filter((entry) => entry.id !== message.id);
                    }, STAGE_CHAT_VISIBLE_MS);
                }
            } catch { /* ignore */ }
        });
        next.addEventListener('reaction', (event) => {
            if (expectedGeneration !== generation) return;
            try {
                const payload = JSON.parse((event as MessageEvent<string>).data) as { name: string; reaction: string; at: number };
                const idValue = Date.now() + Math.random();
                reactions.value = [...reactions.value, { id: idValue, name: payload.name, reaction: payload.reaction }].slice(-20);
                window.setTimeout(() => { reactions.value = reactions.value.filter((reaction) => reaction.id !== idValue); }, REACTION_VISIBLE_MS);
                if (stageReactionsEnabled.value) {
                    stageReactions.value = [...stageReactions.value, { id: idValue, name: payload.name, reaction: payload.reaction }].slice(-3);
                    window.setTimeout(() => { stageReactions.value = stageReactions.value.filter((reaction) => reaction.id !== idValue); }, STAGE_REACTION_VISIBLE_MS);
                }
            } catch { /* ignore */ }
        });
        next.addEventListener('command-result', (event) => {
            if (expectedGeneration !== generation) return;
            try {
                const result = JSON.parse((event as MessageEvent<string>).data) as WatchTogetherCommandResult;
                if (lastCatchUpRequest.value?.id !== result.commandId) return;
                const recipients = lastCatchUpRequest.value.recipients.map((recipient) => recipient.id === result.recipientId && result.sequence > recipient.sequence
                    ? { ...recipient, name: result.name, status: result.status, sequence: result.sequence }
                    : recipient);
                lastCatchUpRequest.value = { ...lastCatchUpRequest.value, recipients };
            } catch { /* ignore */ }
        });
        next.addEventListener('bring-live', (event) => {
            if (expectedGeneration !== generation || !catchUpHandler) return;
            try {
                const command = JSON.parse((event as MessageEvent<string>).data) as { id: string; consentExpiresAt: number };
                if (seenCommands.has(command.id) || command.consentExpiresAt <= Date.now()) return;
                seenCommands.add(command.id);
                void catchUpHandler({ ...command, playbackId: id }).then((status) => {
                    if (playbackId.value === id) void api.watchTogether.commandResult({ playbackId: id, commandId: command.id, status }).catch(() => {});
                });
            } catch { /* ignore */ }
        });
        next.onopen = () => { if (expectedGeneration === generation) { connected.value = true; unavailable.value = false; } };
        next.onerror = () => { if (expectedGeneration === generation) { connected.value = false; unavailable.value = true; } };
    }

    function start(id: string): void {
        if (playbackId.value === id) return;
        stop();
        playbackId.value = id;
        generation += 1;
        const current = generation;
        openSource(id, current);
        heartbeatTimer = window.setInterval(() => { void heartbeat(current); }, HEARTBEAT_MS);
    }

    function setPlaybackSample(sample: WatchTogetherPlaybackSample): void {
        playbackSample = sample;
        lastPlaybackEventAt = sample.measurement.sampledAtMs;
    }

    function setPlaybackReporter(reporter: (() => WatchTogetherPlaybackSample | null) | null): void { playbackReporter = reporter; }

    function setCatchUpHandler(handler: BringLiveHandler | null): void { catchUpHandler = handler; }
    function setConsentPromptActive(active: boolean): void { consentPromptActive.value = active; }

    async function sendChat(text: string): Promise<void> {
        if (!playbackId.value) return;
        await api.watchTogether.chat({ playbackId: playbackId.value, text });
    }
    async function sendReaction(reaction: string): Promise<void> {
        if (!playbackId.value) return;
        await api.watchTogether.reaction({ playbackId: playbackId.value, reaction });
    }
    async function bringEveryoneLive(): Promise<number> {
        if (!playbackId.value) return 0;
        const result = await api.watchTogether.bringLive({ playbackId: playbackId.value });
        lastCatchUpRequest.value = result.command;
        return result.recipients;
    }
    function stop(): void {
        generation += 1;
        if (heartbeatTimer !== null) window.clearInterval(heartbeatTimer);
        heartbeatTimer = null;
        closeSource();
        playbackId.value = null;
        playbackSample = null;
        lastPlaybackEventAt = 0;
        participants.value = [];
        messages.value = [];
        reactions.value = [];
        stageReactions.value = [];
        stageReactionsEnabled.value = false;
        stageMessages.value = [];
        stageChatEnabled.value = false;
        seenStageChatMessages.clear();
        lastCatchUpRequest.value = null;
        eligibleOtherSessionCount.value = 0;
        consentPromptActive.value = false;
        seenCommands.clear();
    }

    return { playbackId, participants, messages, reactions, stageReactionsEnabled, stageReactions, stageChatEnabled, stageMessages, lastCatchUpRequest, eligibleOtherSessionCount, connected, unavailable, consentPromptActive, participantCount, canBringLive, start, stop, setPlaybackSample, setPlaybackReporter, setCatchUpHandler, setConsentPromptActive, sendChat, sendReaction, bringEveryoneLive };
});
